import { c as _c } from "react/compiler-runtime";
import { Button, Dialog, HStack, Portal, Spinner, Text, VStack } from '@chakra-ui/react';
import React, { type ReactNode, useRef, useState } from 'react';
export function CrmLoading(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Spinner size="sm" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = props.label || "Loading";
  let t2;
  if ($[1] !== t1) {
    t2 = <HStack color="fg.muted" gap={2} p={4}>{t0}<Text fontSize="sm">{t1}</Text></HStack>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function CrmEmpty(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props.children) {
    t0 = <Text color="fg.muted" fontSize="sm" px={4} py={6}>{props.children}</Text>;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** Refusals and failures. The error color and role=alert, so it does not read as a hint. */
export function CrmErrorText(props) {
  const $ = _c(2);
  if (!props.children) {
    return null;
  }
  let t0;
  if ($[0] !== props.children) {
    t0 = <Text color="fg.error" fontSize="sm" role="alert">{props.children}</Text>;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * A failed read, with a way to run it again. Every screen that reads the API shows this instead of
 * a spinner that never ends. `onRetry` is absent only for reads that cannot be run again.
 */
export function CrmErrorState(props) {
  const $ = _c(10);
  const t0 = props.title || "Could not load this";
  let t1;
  if ($[0] !== t0) {
    t1 = <Text fontSize="sm" fontWeight="bold">{t0}</Text>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.children) {
    t2 = <Text color="fg.error" fontSize="sm">{props.children}</Text>;
    $[2] = props.children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.onRetry) {
    t3 = props.onRetry ? <Button size="xs" variant="outline" onClick={props.onRetry}>Try again</Button> : null;
    $[4] = props.onRetry;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <VStack align="flex-start" gap={2} p={4} role="alert">{t1}{t2}{t3}</VStack>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Asks before an edit. `onConfirm` may be async: the confirm button shows it is working, ignores
 * repeat clicks until it settles, and the dialog does not close underneath it.
 */
export function CrmConfirmDialog(props: {
  body: ReactNode;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  open: boolean;
  /** The confirm button's color. Red by default, because most of these undo or overwrite something. */
  palette?: 'orange' | 'red';
  title: string;
}) {
  const [pending, setPending] = useState(false);
  // A ref, not the state: two clicks in one frame both see the same stale `pending`.
  const inFlight = useRef(false);
  async function confirm() {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      await props.onConfirm();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  return <Dialog.Root open={props.open} role={'alertdialog'} onOpenChange={({
    open
  }) => {
    if (!open && !inFlight.current) {
      props.onClose();
    }
  }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{props.title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize={'sm'}>{props.body}</Text>
            </Dialog.Body>
            <Dialog.Footer>
              <Button disabled={pending} variant={'outline'} onClick={props.onClose}>
                Cancel
              </Button>
              <Button colorPalette={props.palette || 'red'} loading={pending} onClick={() => void confirm()}>
                {props.confirmLabel}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>;
}